import { joinPath } from "./path.js";
import { resolveRef } from "./schema-context.js";
/**
 * Get props for a child field of an object schema.
 * Use this to easily pass required props to ShapeField.
 *
 * @example
 * ```tsx
 * const schema = { type: 'object', properties: { name: { type: 'string' } } };
 * <ShapeField {...getChildField(schema, 'user', 'name')} />
 * // Equivalent to: <ShapeField path="user.name" schema={nameSchema} parentSchema={schema} fieldName="name" />
 * ```
 */
export function getChildField(parentSchema, parentPath, propertyName) {
    const propSchema = parentSchema.properties?.[propertyName];
    if (!propSchema) {
        throw new Error(`Property "${propertyName}" not found in schema`);
    }
    return {
        path: parentPath ? joinPath(parentPath, propertyName) : propertyName,
        schema: propSchema,
        parentSchema,
        fieldName: propertyName
    };
}
/**
 * Get props for a child field, resolving $ref if present.
 * Use when the parent schema might contain $ref pointers.
 */
export function getChildFieldResolved(ctx, parentSchema, parentPath, propertyName) {
    const resolved = resolveRef(ctx, parentSchema);
    const propSchema = resolved.properties?.[propertyName];
    if (!propSchema) {
        throw new Error(`Property "${propertyName}" not found in schema`);
    }
    // Preserve the original $ref before resolving (for shape-based config lookup)
    const schemaRef = propSchema.$ref;
    return {
        path: parentPath ? joinPath(parentPath, propertyName) : propertyName,
        schema: resolveRef(ctx, propSchema),
        parentSchema: resolved,
        fieldName: propertyName,
        schemaRef
    };
}
/**
 * Get props for an array item field.
 *
 * @example
 * ```tsx
 * const arraySchema = { type: 'array', items: { type: 'string' } };
 * {items.map((item, i) => (
 *   <ShapeField key={item.key} {...getArrayItemField(arraySchema, 'tags', i)} />
 * ))}
 * ```
 */
export function getArrayItemField(arraySchema, arrayPath, index) {
    const itemSchema = Array.isArray(arraySchema.items)
        ? arraySchema.items[0]
        : arraySchema.items;
    if (!itemSchema) {
        throw new Error('Array schema has no items definition');
    }
    return {
        path: joinPath(arrayPath, index),
        schema: itemSchema
    };
}
/**
 * Get props for an array item field, resolving $ref if present.
 */
export function getArrayItemFieldResolved(ctx, arraySchema, arrayPath, index) {
    const resolved = resolveRef(ctx, arraySchema);
    const rawItemSchema = Array.isArray(resolved.items)
        ? resolved.items[0]
        : resolved.items;
    if (!rawItemSchema) {
        throw new Error('Array schema has no items definition');
    }
    return {
        path: joinPath(arrayPath, index),
        schema: resolveRef(ctx, rawItemSchema)
    };
}
/**
 * Create a scoped field helper for an object schema.
 * Returns a function that generates field props by property name.
 *
 * @example
 * ```tsx
 * function UserForm({ schema, path }: { schema: FormSchema; path: string }) {
 *   const field = createFieldHelper(schema, path);
 *   return (
 *     <>
 *       <ShapeField {...field('name')} />
 *       <ShapeField {...field('email')} />
 *       <ShapeField {...field('age')} />
 *     </>
 *   );
 * }
 * ```
 */
export function createFieldHelper(parentSchema, parentPath) {
    return (propertyName) => getChildField(parentSchema, parentPath, propertyName);
}
/**
 * Create a scoped field helper that resolves $ref pointers.
 */
export function createFieldHelperResolved(ctx, parentSchema, parentPath) {
    return (propertyName) => getChildFieldResolved(ctx, parentSchema, parentPath, propertyName);
}
